import { Injectable } from '@angular/core';
import { 
  Firestore, 
  collection, 
  getDocs, 
  doc, 
  setDoc, 
  collectionGroup, 
  DocumentReference 
} from '@angular/fire/firestore';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from './auth.service';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class MissionService {
  private currentMissionSubject = new BehaviorSubject<any>(null);
  public currentMission$ = this.currentMissionSubject.asObservable();
  
  private missionResultSubject = new BehaviorSubject<{
    show: boolean;
    isCorrect: boolean;
  }>({ show: false, isCorrect: false });
  public missionResult$ = this.missionResultSubject.asObservable();

  constructor(
    private firestore: Firestore,
    private authService: AuthService,
    private toastr: ToastrService
  ) {}

  /**
   * Lấy tất cả nhiệm vụ và xác định nhiệm vụ hiện tại của người dùng
   * Giới hạn: mỗi ngày chỉ trả lời 1 nhiệm vụ
   */
  public async fetchAllMission(): Promise<void> {
    try {
      // Giới hạn mỗi ngày chỉ trả lời 1 lần
      const today = new Date().toISOString().slice(0, 10); // yyyy-mm-dd
      const lastAnswered = localStorage.getItem('missionAnsweredDate');
      if (lastAnswered === today) {
        this.currentMissionSubject.next(null);
        return;
      }

      const currentUser = this.authService.userSubject.value;
      if (!currentUser) {
        console.log('Chưa có thông tin người dùng, thử lại sau 500ms');
        setTimeout(() => this.fetchAllMission(), 500);
        return;
      }

      const userId = currentUser.uid;

      // Lấy tất cả mission
      const missionRef = collection(this.firestore, 'mission');
      const missionSnapshot = await getDocs(missionRef);
      const allMissions = missionSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      console.log('🔥 Tất cả mission:', allMissions);
      
      if (allMissions.length === 0) {
        console.log('Không tìm thấy nhiệm vụ nào, thử lại sau 500ms');
        setTimeout(() => this.fetchAllMission(), 500);
        return;
      }

      // Lấy tất cả completion của user
      const completionRef = collectionGroup(this.firestore, 'completion');
      const completedSnapshot = await getDocs(completionRef);
      const completedMissionIds = completedSnapshot.docs
        .filter(doc => doc.id === userId)
        .map(doc => doc.ref.parent.parent?.id)
        .filter((id): id is string => !!id);

      console.log('✅ Completion của user:', completedMissionIds);

      let uncompleted;
      if (completedMissionIds.length === 0) {
        uncompleted = allMissions[0]; // Nếu chưa làm gì thì lấy nhiệm vụ đầu tiên
      } else {
        uncompleted = allMissions.find(m => !completedMissionIds.includes(m.id));
      }

      console.log('🎯 Current mission:', uncompleted);

      this.currentMissionSubject.next(uncompleted || null);

    } catch (error) {
      console.error('🚨 Lỗi khi load mission:', error);
      // Thử lại sau khi gặp lỗi
      setTimeout(() => this.fetchAllMission(), 1000);
    }
  }

  /**
   * Kiểm tra câu trả lời của người dùng
   * @param userAnswer Câu trả lời của người dùng
   * @param userDocRef Tham chiếu đến document người dùng
   * @param updateDiamonds Callback để cập nhật 🏵️
   */
  public checkAnswer(
    userAnswer: boolean, 
    userDocRef: DocumentReference | null,
    updateDiamonds: (amount: number) => void
  ): void {
    const currentMission = this.currentMissionSubject.value;
    if (!currentMission) return;
    
    // Đánh dấu đã trả lời hôm nay
    const today = new Date().toISOString().slice(0, 10);
    localStorage.setItem('missionAnsweredDate', today);

    // So sánh câu trả lời của người dùng với câu trả lời đúng
    const isCorrect = userAnswer === currentMission.answer;
    
    // Cập nhật trạng thái kết quả nhiệm vụ
    this.missionResultSubject.next({
      show: true,
      isCorrect: isCorrect
    });

    // Tự động ẩn kết quả sau 3 giây
    setTimeout(() => {
      this.missionResultSubject.next({
        show: false,
        isCorrect: false
      });
      // Ẩn luôn nhiệm vụ sau khi hết kết quả
      this.currentMissionSubject.next(null);
    }, 3000);
    
    if (isCorrect && userDocRef) {
      this.toastr.success('Bạn đã hoàn thành nhiệm vụ!', 'Chúc mừng!');
      updateDiamonds(currentMission.CollectedCoin || 1);
      this.recordMissionCompletion(currentMission.id);
    }
  }

  /**
   * Ghi nhận việc hoàn thành nhiệm vụ
   * @param missionId ID của nhiệm vụ đã hoàn thành
   */
  public async recordMissionCompletion(missionId: string): Promise<void> {
    const currentUser = this.authService.userSubject.value;
    if (!currentUser) return;

    const userId = currentUser.uid;
    const completionRef = doc(
      this.firestore,
      `mission/${missionId}/completion/${userId}`
    );

    try {
      await setDoc(completionRef, {
        completed: true,
        completedAt: new Date()
      });

      console.log('Mission completion recorded for user:', userId);
      
      // Sau khi hoàn thành nhiệm vụ hiện tại, tải nhiệm vụ tiếp theo
      this.fetchAllMission();
    } catch (error) {
      console.error('Error recording mission completion:', error);
    }
  }

  /**
   * Hiển thị hoặc ẩn giao diện nhiệm vụ
   * @param show True để hiển thị, False để ẩn
   */
  public toggleMissionUI(show: boolean): void {
    // Nếu ẩn, đồng thời reset kết quả
    if (!show) {
      this.missionResultSubject.next({
        show: false,
        isCorrect: false
      });
    }
  }
}